<template>
    <v-form
        ref="form"
        v-model="valid"
        class="p-3 pt-0"
        @submit="onSubmit">

        <!-- client account -->
        <v-select
            v-model="form.client_name"
            :items="accounts"
            :error-messages="clientNameErrors"
            :rules="[v => !!v || 'Account is required']"
            label="Link Account"
            required
            :disabled="isAdded"
            v-if="newAccount && !forEdit"
            @change="showAccountForm()">
        </v-select>

        <!-- client name -->
        <v-text-field
            v-model="form.client_name"
            :error-messages="clientNameErrors"
            :counter="30"
            label="Client Name"
            required
            @input="$v.form.client_name.$touch()"
            @blur="$v.form.client_name.$touch()"
            :disabled="isAdded"
            v-if="linkAccount && show_account_fields">
        </v-text-field>

        <!-- client notes -->
        <v-textarea
            outlined
            label="Client Notes"
            v-model="form.client_notes"
            class="mt-5"
            :disabled="isAdded"
            v-if="linkAccount && show_account_fields">
        </v-textarea>

        <!-- client user -->
        <v-autocomplete
            v-model="form.user_id"
            :items="users"
            :error-messages="userIdErrors"
            :rules="[v => !!v || 'User is required']"
            dense
            flat
            clearable
            label="Choose User"
            required
            :disabled="isAdded"
            v-if="linkAccount && show_account_fields"
            @change="showUserForm()">
        </v-autocomplete>

        <UserForm
        ref="userdetails"
        v-if="is_disabled && allow_user"
        :is_disabled="is_disabled"
        :isAdded="isAdded"/>

        <div class="mt-3">
            <v-btn type="submit" small class="mr-1" color="success" :readonly="isAdded">
                <span v-if="isAdded && is_disabled">
                    Account/User Added
                </span>
                <span v-else>{{ btnTxt }}</span>
            </v-btn>
            <v-btn small @click="clear" v-if="!isAdded">clear</v-btn>
        </div>
    </v-form>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, maxLength, numeric } from 'vuelidate/lib/validators'
import { EventBus } from '@/js/event-bus.js'
import UserForm from '~/users/UserForm';
import { mapGetters } from 'vuex'

export default {
    components: {
        UserForm
    },

    mixins: [validationMixin],
    validations: {
        form: {
            client_name: { required, maxLength: maxLength(30) },
            user_id: { required, numeric }
        }
    },

    props: {
        submitText:  { type: String, default: 'Add' },
        submit:      { type: Function, default: () => {} },
        clientName:  { type: String, default: '' },
        clientNotes: { type: String, default: '' },
        emails:      { type: Array, default: [] },
        userId:      { type: Number, default: 0 },
        is_disabled: { type: Boolean, default: false },
        newAccount:  { type: Boolean, default: false },
        linkAccount: { type: Boolean, default: false },
        forEdit:     { type: Boolean, default: false },
    },

    data() {
        return {
            valid: false,

            accounts: [],
            form: {
                client_name: this.clientName,
                client_notes: this.clientNotes,
                emails: this.emails,
                user_id: this.userId,
                link_account: false
            },
            isAdded: false,
            allow_user: false,
            show_account_fields: this.forEdit,
            totalUser:   0,
            isValid:     0,
            btnTxt: this.submitText
        }
    },

    created() {

        this.$http
            .get('/api/accounts', {
                params: {
                    limit: 0
                }
            })
            .then(({ data }) => {

                this.accounts = data.data.map(account => {
                    return { value: parseInt(account.id), text: account.attributes.client_name }
                })

                if (this.is_disabled) {
                    this.accounts.unshift({
                        text: 'ADD NEW ACCOUNT',
                        value: "New Account"
                    })
                }
            })
            .catch(err => {
                EventBus.$emit('show-error', 'Failed to fetch accounts.');
            })
    },

    computed: {
        clientNameErrors() {
            const errors = []
            !this.$v.form.client_name.maxLength && errors.push('Client name must be at most 30 characters long')
            !this.$v.form.client_name.required && errors.push('Client name is required.')
            return errors
        },

        userIdErrors() {
            const errors = []
            if (!this.$v.form.user_id.$dirty) return errors
            !this.$v.form.user_id.required && errors.push('Bind to user is required.')
            return errors
        },

        ...mapGetters('UsersState', {
            userList: 'getUserList'
        }),

        users() {
            return this.userList?.map(user => {
                return { value: parseInt(user.id), text: user.name }
            }) ?? []
        }
    },

    methods: {
        checkWork(data) {
            this.isAdded = data
        },

        isUserAllowed() {
            return this.allow_user
        },

        isAccountAllowed() {
            return this.link_account
        },

        getNewData() {
            return this.$refs.userdetails.userForm()
        },

        onSubmit(e) {
            this.$v.form.$touch();
            e.preventDefault();

            // for adding integration
            if (this.is_disabled) {
                // if has new user form
                if (this.allow_user) {
                    let isValid = typeof this.$refs.userdetails !== 'undefined' ? this.$refs.userdetails.triggerValidation() : 0

                    if (this.valid && isValid && !this.isAdded) {
                        // call callback submit
                        this.submit(this.form)
                        this.isAdded = true
                    } else {
                        if (!this.isAdded) {
                            EventBus.$emit('show-error', 'Failed! Please try again.');
                        }
                        this.isAdded = this.isAdded ? true : false
                    }
                }
                else {
                    if (this.valid && !this.isAdded) {
                        // call callback submit
                        this.submit(this.form)
                        this.isAdded = true
                    } else {
                        this.isAdded = this.isAdded ? true : false
                    }
                }
            }
            else {
                if (this.valid) {
                    // call callback submit
                    this.submit(this.form)
                } else {
                    EventBus.$emit('show-error', 'Failed! Please try again.');
                }
            }
        },

        clear() {
            this.$refs.form.reset()
        },

        showUserForm() {
            this.allow_user = false

            if (this.form.user_id == '0.1') {
                this.allow_user = true
            }
        },

        showAccountForm() {
            this.btnTxt = "Link"
            this.show_account_fields = false
            this.form.link_account = true;
            if (this.form.client_name == 'New Account') {
                this.btnTxt = "Add"
                this.form.client_name = "New Account"
                this.show_account_fields = true
                this.form.link_account = false;
            }
        },

        onRemove(itemMeta) {
            let index = this.items.findIndex(item => item == itemMeta)
            this.items.splice(index, 1)
            if (Number.isInteger(itemMeta.id)) {
                this.$http
                    .delete('/api/integrationmetas/' + itemMeta.id)
                    .then(() => {
                        EventBus.$emit('show-success', 'Successfully deleted integration meta.');
                    })
                    .catch(err => {
                        EventBus.$emit('show-error', err)
                    })
            }
        },
    }
}
</script>